<template>
<div class="form-wrapper">
    <div class="confirmation">
        <logo-header :title="'Verify Your Email'" :designVersion="2"></logo-header>
        <div class="body">
            <div class="spinner" :class="{'hidden': !isSubmitting}"><i class="fa fa-spinner fa-pulse"></i></div>
            <div :class="{'hidden': isSubmitting}">
                <div class="paper-plane-icon-wrapper">
                    <img id="paper-plane-icon" src="/assets/images/paper-plane.svg" alt="paper-plane-icon">
                </div>
                <paragraph id="instruction" :type="'p'" :variation="'large'" :alignment="'center'">Please follow the instructions in the verification email to activate your account. Your first scan will start automatically after clicking the verification link.</paragraph>
                <div class="button-wrapper">
                    <custom-button v-if="!isSubmitting" :text="'Resend Verification Link'" @click="resendLink" :size="'button-large'"></custom-button>
                    <button v-else id="mock-button-to-maintain-form-height" class="btn btn-primary hidden" :isActive="false">Resend Verification Code</button>
                    <label class="label" v-show="resentMsg">{{ resentMsg }}</label>
                </div>
            </div>
        </div>
    </div>
</div>
</template>

<script>
/* eslint-disable import/extensions */
/* eslint-disable import/no-extraneous-dependencies */
import '~/assets/js/global.js';
import Amplify from '@aws-amplify/core';
import Auth from '@aws-amplify/auth';
import LogoHeader from './logo-header.vue';
import Validate from '~/mixins/validation.js';
import { getBrandingVar } from '~/assets/js/utils/whitelabeling.js';
import Heading from '~/components/shared/heading.vue';
import Paragraph from '~/components/shared/paragraph.vue';
import CustomButton from '~/components/shared/custom-button.vue';

Amplify.configure(getBrandingVar('aws_config_eu_central'));

const LOCALSTORAGE_KEY_STATE = 'LOCALSTORAGE_KEY_STATE';
const SANE_TOKEN_PATTERN = /^[a-zA-Z0-9\-_]+\.[a-zA-Z0-9\-_]+\.[a-zA-Z0-9\-_]+$/;
const SANE_TOKEN_MAX_LENGTH = 2048;
const URL_NON_SANE_TOKEN = '/login';
const COOKIE_KEY_TEMP_TOKEN = 'temp_id_token';
const TEMP_COOKIE_EXPIRY_SECONDS = 180;

export default {
    components: {
        // eslint-disable-next-line vue/no-unused-components
        LogoHeader, Heading, Paragraph, CustomButton,
    },
    mixins: [Validate],
    data() {
        return {
            errors: [],
            code: '',
            isSubmitting: false,
            resentMsg: '',
            resentClass: 'text-success',
        };
    },
    props: {
        emailToVerify: String,
        checkedPassword: String,
    },
    methods: {
        resendLink() {
            const self = this;
            self.isSubmitting = true;
            self.resentMsg = '';
            Auth.resendSignUp(this.emailToVerify).then(() => {
                self.isSubmitting = false;
            }).catch((e) => {
                self.resentClass = 'text-primary';
                self.isSubmitting = false;
                if (e.code === 'InvalidParameterException') {
                    self.resentMsg = e.message;
                } else {
                    self.resentMsg = 'There was an error when sending the code. Please try again.';
                }
            });
        },
        signIn() {
            const self = this;
            Auth.signIn(this.emailToVerify, this.checkedPassword)
                .then((user) => {
                    self.setCookie(user);
                    self.isSubmitting = false;
                }).catch((err) => {
                    // eslint-disable-next-line no-console
                    console.log(err);
                });
        },
        setCookie(user) {
            const token = user.signInUserSession.idToken.jwtToken;
            let redirectUrl = '/';
            if ((token && token.length <= SANE_TOKEN_MAX_LENGTH && SANE_TOKEN_PATTERN.test(token))) {
                localStorage.removeItem(LOCALSTORAGE_KEY_STATE);
                sessionStorage.clear();
                document.cookie = `${COOKIE_KEY_TEMP_TOKEN}=${token}; path=/; SameSite=Strict; expires=${this.getCurrentTimePlus(TEMP_COOKIE_EXPIRY_SECONDS)}; secure;`;
                // eslint-disable-next-line no-console
                this.doBackendRequest('GET', '/auth/login', () => { console.log('success'); }, undefined, undefined);
            } else {
                // Bogus or no token
                document.cookie = `${COOKIE_KEY_TEMP_TOKEN}=; path=/; SameSite=Strict; expires=Thu, 01 Jan 1970 00:00:00 GMT; secure;`;
                redirectUrl = URL_NON_SANE_TOKEN;
                window.location = redirectUrl;
            }
        },
        getCurrentTimePlus(seconds) {
            const date = new Date();
            date.setTime(date.getTime() + (seconds * 1000));
            return date.toGMTString();
        },
    },
};
</script>

<style lang="scss" scoped>
.login-text {
    margin-top: 0.5rem;
    margin-bottom: 0;
    font-size: 0.9rem;
}
.spinner {
    position: absolute;
    font-size: 3.7rem;
    left: 50%;
    top: 57%;
    transform: translate(-50%,-50%);
    color: $color-neutral-light-500;
}

.hidden {
    visibility: hidden;
}
.label {
    font-size: $font-size-label;
    color: $color-semantic-red-200 !important;
    margin-top: 1rem;
}
.form-wrapper {
    .confirmation {
        @media only screen and (min-width: 601px) {
            padding-top: 2rem;
            padding-bottom: 2rem;
        }
    }

    .paper-plane-icon-wrapper {
        text-align: center;
        margin-bottom: 2rem;

        #paper-plane-icon {
            width: 24%;
            height: auto;
        }
    }

    #instruction {
        color: $color-neutral-dark-300;
    }

    .button-wrapper {
        text-align: center;

        .btn-primary {
            font-size: $font-size-button-lg;
            padding: 4px 16px;
        }

        .fa {
            font-size: $font-size-button-lg;
        }
    }
}
</style>
